import { cn } from '@/utils/cn'
import type { SuggestionChip } from '@/types'
import { SuggestionChipItem } from './SuggestionChipItem'

export interface SuggestionChipsProps {
  chips: SuggestionChip[]
  onSelect: (text: string) => void
  disabled?: boolean
  className?: string
}

/**
 * Tappable follow-up prompts shown beneath an assistant message. Selecting a
 * chip sends its text as the next message. Every chip shares one pill style,
 * including the handoff chip, which dials a care advocate instead.
 *
 * `disabled` applies to chips that send a message. Handoff chips and link
 * chips with an absolute http(s) url stay live, since dialing a care advocate
 * or opening the member portal does not interact with the reply in flight; a
 * link chip without a usable url falls back to a message-sending chip and
 * disables with it.
 */
export function SuggestionChips({
  chips,
  onSelect,
  disabled = false,
  className,
}: SuggestionChipsProps) {
  if (chips.length === 0) return null

  return (
    <div
      className={cn('my-2 flex flex-wrap gap-2', className)}
      role="group"
      aria-label="Suggested follow-ups"
    >
      {chips.map((chip, idx) => (
        <SuggestionChipItem
          key={`${idx}-${chip.text}`}
          text={chip.text}
          kind={chip.kind}
          url={chip.url}
          onSelect={onSelect}
          disabled={disabled}
        />
      ))}
    </div>
  )
}
